import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { CheckCircle, Crown, Heart, ArrowRight, Loader2, RefreshCw } from 'lucide-react';
import api from '../lib/api';
import { toast } from 'sonner';
import Footer from '../components/Footer';

export default function PlansPage() {
  const { t, lang, formatCurrency } = useLanguage();
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [checkoutLoading, setCheckoutLoading] = useState(null);
  const [syncLoading, setSyncLoading] = useState(false);
  const [pendingOrder, setPendingOrder] = useState(null);

  useEffect(() => {
    api.get('/plans').then(r => { setPlans(r.data.plans || []); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  const handleCheckout = async (planId) => {
    if (!user) { navigate('/auth'); return; }
    if (planId === 'free') { toast.info('You are already on the Free plan'); return; }
    setCheckoutLoading(planId);
    try {
      const res = await api.post('/subscriptions/checkout', { plan_id: planId, email: '' });
      setPendingOrder({
        order_id: res.data.order_id,
        order_name: res.data.order_name,
        amount: res.data.amount,
        plan_name: res.data.plan_name,
        plan_id: planId,
      });
      toast.success(`Order ${res.data.order_name} created! Complete payment to activate your ${res.data.plan_name} plan.`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Checkout failed');
    } finally {
      setCheckoutLoading(null);
    }
  };

  const handleSync = async () => {
    if (!user) return;
    setSyncLoading(true);
    try {
      const res = await api.post('/subscriptions/sync');
      if (res.data.synced > 0) {
        toast.success('Payment verified! Your plan has been activated.');
        refreshUser();
      } else {
        toast.info('No completed payments found yet. Please complete payment first.');
      }
    } catch (e) {
      toast.error('Could not verify payment');
    } finally {
      setSyncLoading(false);
    }
  };

  const currentPlan = user?.subscription?.plan || 'free';

  if (loading) return (
    <div className="min-h-[50vh] flex items-center justify-center">
      <div className="w-8 h-8 border-3 border-[#D9381E] border-t-transparent rounded-full animate-spin" />
    </div>
  );

  return (
    <div data-testid="plans-page">
      <section className="py-16 bg-[#FFF8F0]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <Badge className="bg-[#F2A900]/20 text-[#F2A900] border-[#F2A900]/30 mb-4">
              <Crown className="w-3.5 h-3.5 mr-1" /> Premium
            </Badge>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#2D2424]">{t('plans.title')}</h1>
            <p className="mt-4 text-base text-[#78716C] max-w-xl mx-auto">{t('plans.subtitle')}</p>
          </div>

          {/* Pending Order Banner */}
          {pendingOrder && (
            <div className="max-w-lg mx-auto mb-8 p-5 bg-white rounded-2xl border-2 border-[#F2A900] shadow-lg" data-testid="pending-order-banner">
              <div className="text-center">
                <div className="w-12 h-12 bg-[#F2A900]/10 rounded-full flex items-center justify-center mx-auto mb-3">
                  <Crown className="w-6 h-6 text-[#F2A900]" />
                </div>
                <h3 className="text-lg font-bold text-[#2D2424] mb-1">Order Created - {pendingOrder.plan_name} Plan</h3>
                <p className="text-sm text-[#78716C] mb-1">Order {pendingOrder.order_name}</p>
                <p className="text-2xl font-bold text-[#D9381E] mb-3">&#8377;{pendingOrder.amount?.toLocaleString('en-IN')}</p>
                <p className="text-xs text-[#78716C] mb-4">Complete payment via the Shopify store, then click Verify Payment.</p>
                <div className="flex gap-2 justify-center">
                  <Button
                    onClick={handleSync}
                    disabled={syncLoading}
                    className="bg-[#D9381E] hover:bg-[#B91C1C] text-white rounded-full px-6"
                    data-testid="verify-order-btn"
                  >
                    <RefreshCw className={`w-4 h-4 mr-1 ${syncLoading ? 'animate-spin' : ''}`} />
                    {syncLoading ? 'Verifying...' : 'Verify Payment'}
                  </Button>
                  <Button variant="outline" onClick={() => setPendingOrder(null)} className="rounded-full border-[#E7E5E4]">
                    Dismiss
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* Verify Payment for existing pending orders */}
          {!pendingOrder && user && currentPlan === 'free' && (
            <div className="text-center mb-6">
              <Button onClick={handleSync} disabled={syncLoading} variant="outline"
                className="rounded-full gap-2 border-[#F2A900] text-[#F2A900] hover:bg-[#F2A900]/10"
                data-testid="verify-payment-btn">
                <RefreshCw className={`w-4 h-4 ${syncLoading ? 'animate-spin' : ''}`} />
                {syncLoading ? 'Verifying...' : 'Verify Payment'}
              </Button>
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {plans.map((plan) => {
              const isCurrent = currentPlan === plan.id;
              return (
                <Card
                  key={plan.id}
                  className={`relative p-6 rounded-2xl border-2 transition-all hover:-translate-y-1 hover:shadow-xl ${
                    plan.is_popular ? 'border-[#F2A900] shadow-lg plan-card-popular' : 'border-[#E7E5E4]'
                  } ${isCurrent ? 'ring-2 ring-green-500' : ''}`}
                  data-testid={`plan-card-${plan.id}`}
                >
                  {plan.is_popular && (
                    <Badge className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#F2A900] text-[#2D2424] px-4 py-1 text-xs font-bold rounded-full">
                      <Crown className="w-3 h-3 mr-1" /> {t('plans.popular')}
                    </Badge>
                  )}
                  {isCurrent && (
                    <Badge className="absolute -top-3 right-4 bg-green-600 text-white px-3 py-1 text-xs font-bold rounded-full">
                      {t('plans.current')}
                    </Badge>
                  )}

                  <div className="text-center mb-6 pt-2">
                    <h3 className="text-xl font-bold text-[#2D2424]">{plan[`name_${lang}`] || plan.name}</h3>
                    <div className="mt-3">
                      {plan.price === 0 ? (
                        <div className="text-2xl font-bold text-green-600">{t('plans.free_tag')}</div>
                      ) : (
                        <>
                          <span className="text-4xl font-bold text-[#2D2424]">{formatCurrency(plan.price)}</span>
                          <span className="text-sm text-[#78716C] ml-1">/ {plan.duration_months} {lang === 'en' ? 'months' : lang === 'hi' ? 'महीने' : 'महिने'}</span>
                        </>
                      )}
                    </div>
                    <p className="text-xs text-[#78716C] mt-2">{plan[`description_${lang}`] || plan.description}</p>
                  </div>

                  <ul className="space-y-2.5 mb-6">
                    {Object.entries(plan.features || {}).map(([key, val]) => {
                      const FEATURE_LABELS = {
                        interests_per_day: { en: 'interests/day', hi: 'रुचि/दिन', mr: 'आवड/दिवस' },
                        active_chats: { en: 'active chats', hi: 'सक्रिय चैट', mr: 'सक्रिय चॅट' },
                        view_contacts: { en: 'View contact details', hi: 'संपर्क विवरण देखें', mr: 'संपर्क तपशील पहा' },
                        boost_per_month: { en: 'Profile boost/month', hi: 'प्रोफ़ाइल बूस्ट/माह', mr: 'प्रोफाइल बूस्ट/महिना' },
                        video_call: { en: 'Video calling', hi: 'वीडियो कॉल', mr: 'व्हिडिओ कॉल' },
                        voice_call: { en: 'Voice calling', hi: 'वॉयस कॉल', mr: 'व्हॉइस कॉल' },
                        featured_profile: { en: 'Featured profile', hi: 'फ़ीचर्ड प्रोफ़ाइल', mr: 'फीचर्ड प्रोफाइल' },
                        relationship_manager: { en: 'Relationship manager', hi: 'रिलेशनशिप मैनेजर', mr: 'रिलेशनशिप मॅनेजर' },
                      };
                      const label = FEATURE_LABELS[key]?.[lang] || FEATURE_LABELS[key]?.en || key.replace(/_/g, ' ');
                      const displayVal = val === -1 ? t('plans.unlimited') : val === true ? '' : val === false ? '' : val;
                      return (
                        <li key={key} className="flex items-center gap-2.5 text-sm">
                          <CheckCircle className={`w-4 h-4 flex-shrink-0 ${val === false ? 'text-[#E7E5E4]' : 'text-green-600'}`} />
                          <span className={val === false ? 'text-[#78716C] line-through' : 'text-[#2D2424]'}>
                            {displayVal} {label}
                          </span>
                        </li>
                      );
                    })}
                  </ul>

                  <Button
                    onClick={() => handleCheckout(plan.id)}
                    disabled={isCurrent || checkoutLoading === plan.id}
                    className={`w-full rounded-full py-5 font-semibold transition-all ${
                      plan.is_popular
                        ? 'bg-[#D9381E] hover:bg-[#B91C1C] text-white shadow-lg'
                        : isCurrent
                        ? 'bg-green-600 text-white cursor-default'
                        : 'bg-[#2D2424] hover:bg-[#2D2424]/90 text-white'
                    }`}
                    data-testid={`checkout-${plan.id}-btn`}
                  >
                    {checkoutLoading === plan.id ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : isCurrent ? (
                      <>{t('plans.current')}</>
                    ) : (
                      <>{t('plans.choose')} <ArrowRight className="w-4 h-4 ml-1" /></>
                    )}
                  </Button>
                </Card>
              );
            })}
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
}
